import PrimaryHeader from '@/components/PrimaryHeader';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Transition } from '@headlessui/react';
import AnnouncementBanner from '@/pages/AnnouncementBanner';
import CTACard from '@/components/ui/CTACard';
import useCTAs from '@/hooks/useCTAs';
import ExploreCardsHomeScreen from '@/components/ui/ExploreCardsHomeScreen';
import { HomeVideos } from '@/features/home-videos';

// How many CTA cards to display at once — this is a layout decision, so it
// lives here rather than inside the useCTAs hook.
const MAX_CTAS = 3;

// A completed card gets a brief moment on screen before it's removed. Once
// the cards have loaded, if a completed card is showing (and none is
// yet exiting), wait this long, then report it as shown so the hook can
// start removing it — see the effect below.
const COMPLETED_CARD_SHOWN_DELAY_MS = 500;

export default function StreamlinedLayout(props) {
  const { currentUser } = props;
  const navigate = useNavigate();
  const {
    ctas,
    isLoading: isLoadingCtas,
    exitingId,
    onCardShown,
    onCardExited,
  } = useCTAs(currentUser, MAX_CTAS);

  const handleExploreCardClick = useCallback((exploreCardId) => {
    switch (exploreCardId) {
      case 'explore-offers':
        navigate('/offers-populate');
        break;
      case 'explore-projects':
        navigate('/opportunities');
        break;
      case 'resources':
        navigate('/my-network');
        break;
      default:
        break;
    }
  }, [navigate]);

  // Completed cards aren't marked shown as soon as they appear like other
  // cards — instead, once the cards have loaded, if at least one completed
  // card is showing, wait a short beat and then report only the first one
  // as shown, which kicks off its exit. Once it's actually gone
  // (onCardExited fires, clearing exitingId), this re-checks the list: if
  // another completed card is still showing, the same process repeats.
  useEffect(() => {
    if (isLoadingCtas || exitingId) return undefined;

    const firstCompletedCta = ctas.find((cta) => cta.state === 'success');
    if (!firstCompletedCta) return undefined;

    const timer = setTimeout(() => {
      onCardShown(firstCompletedCta.id);
    }, COMPLETED_CARD_SHOWN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [ctas, isLoadingCtas, exitingId, onCardShown]);

  return (
    <div>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <PrimaryHeader
        style="simple"
        title={(
          <div className="text-4xl font-semibold text-cs-gray-900 space-x-4">
            <span>{`Welcome back${currentUser.data.firstName ? `, ${currentUser.data.firstName}!` : '!'}`}</span>
            <span>🎉</span>
          </div>
        )}
        textClassName="text-cs-orange-500"
      />
      <AnnouncementBanner />
      <div className="px-4">
        {isLoadingCtas && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 rounded-2xl bg-white border-2 border-gray-100">
            <div className="text-lg font-semibold md:col-span-3">
              Steps to get you started
            </div>
            <div className="md:col-span-3 h-24 animate-pulse rounded-xl bg-gray-50" />
          </div>
        )}
        {!isLoadingCtas && ctas.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 rounded-2xl bg-white border-2 border-gray-100">
            <div className="text-lg font-semibold md:col-span-3">
              Steps to get you started
            </div>
            {ctas.map((cta) => {
              // Completed cards are reported shown by the delayed check
              // above rather than as soon as they finish entering.
              const isCompleted = cta.state === 'success';

              return (
                <Transition
                  key={cta.id}
                  appear
                  show={cta.id !== exitingId}
                  enter="transition-opacity duration-75"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterEnter={isCompleted ? undefined : () => onCardShown(cta.id)}
                  afterLeave={() => onCardExited(cta.id)}
                >
                  <CTACard {...cta} />
                </Transition>
              );
            })}
          </div>
        )}
      </div>
      <div className="px-4 my-6 empty:hidden">
        <HomeVideos />
      </div>
      <div className="px-4">
        <div className="text-lg mb-4 font-semibold">Need more information?</div>
        <ExploreCardsHomeScreen onCardClick={handleExploreCardClick}/>
      </div>
    </div>
  );
}

StreamlinedLayout.propTypes = {
  currentUser: PropTypes.shape({
    data: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string
    })
  }).isRequired,
};
